import React from 'react';
import { StyledNuvoBox } from '#/common/styles/template';
import ColumnMapper, { OptionInfo, TargetField } from '#/common/components/ColumnMapper';
import { useViewport } from '@nuvolo/nuux-theme/hooks/useViewport';
import { NuvoLabel } from '@nuvolo/nuux-core/components/NuvoLabel';
import styled from 'styled-components';
import { color } from '@nuvolo/nuux-theme/utils';

const WarningLabel = styled(NuvoLabel)`
  color: ${color('primary', 'dark')};
  background-color: ${color('warn', 'middle', 0.15)};
  border: 1px solid ${color('warn', 'middle')} !important;
  border-radius: 20px;
  padding: 10px 20px;
  font-size: 14px;
  position: absolute;
  margin-right: 250px;
`;

// Define the mandatory fields
const mandatoryFields = [
  {
    key: 'asset_tag',
    label: 'Asset Tag',
    isDropdown: true,
    required: true,
    unique: true,
    noUnknownOption: true,
    values: [
      { key: 'asset_tag', label: 'Asset Tag' },
      { key: 'serial_number', label: 'Serial Number' },
    ],
  },
  { key: 'asset_tag', label: 'Asset Tag', required: true },
  { key: 'serial_number', label: 'Serial Number', required: true },
  { key: 'asset_type', label: 'Asset Type', required: true },
  { key: 'manufacturer', label: 'Manufacturer', required: true },
  { key: 'model', label: 'Model', required: true },
  { key: 'description', label: 'Description', required: true },
  { key: 'location_country', label: 'Country', required: true, noUnknownOption: true },
  { key: 'location_state', label: 'State', required: true, noUnknownOption: true },
  { key: 'location_city', label: 'City', required: true, noUnknownOption: true },
  { key: 'location_building', label: 'Building', required: true, noUnknownOption: true },
] as TargetField[];

export interface MapMandatoryFieldsStepProps {
  csvColumns: OptionInfo[];
  mapping: Record<string, string>;
  setMapping: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  targetDropdownSelections: Record<string, string>;
  setTargetDropdownSelections: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setIsValid: React.Dispatch<React.SetStateAction<boolean>>;
}

const MapMandatoryFieldsStep: React.FC<MapMandatoryFieldsStepProps> = ({
  csvColumns,
  mapping,
  setMapping,
  targetDropdownSelections,
  setTargetDropdownSelections,
  setIsValid,
}) => {
  const { isDesktop } = useViewport();

  return (
    <StyledNuvoBox isDesktop={isDesktop}>
      <div className="box-caption">
        <NuvoLabel>Match Mandatory Columns</NuvoLabel>
      </div>
      {csvColumns.length === 0 ? (
        <NuvoLabel>Loading CSV columns...</NuvoLabel>
      ) : (
        <ColumnMapper
          sourceOptions={csvColumns}
          targetFields={mandatoryFields}
          mapping={mapping}
          setMapping={setMapping}
          targetDropdownSelections={targetDropdownSelections}
          setTargetDropdownSelections={setTargetDropdownSelections}
          setIsValid={setIsValid}
          uniqueFieldsMessage="This will be your unique identifier field. The value should be unique for each asset."
          uniqueFieldsMessageSeeMore="The unique identifier is used to define the asset unequivocally and if another unit with the same unique identifier is uploaded, its information might update or supersede the existing record. The content of the other fields will not be used by the system to establish uniqueness of the record."
          sourceLabel="Your File Columns"
          targetLabel="TMM Columns"
          separators={[{ beforeColumnKey: 'location_country', label: 'Location' }]}
        />
      )}
      <WarningLabel>
        If there is missing information in your CSV file we are going to save the fields with value
        Unknown, so you can fill them later. All assets will be imported with default status
        Installed. <br />
        Note: for Location Hierarchy items there is no Unknown option, due to data quality
        considerations. Ensure that there is a Country, State, City, Building column in your data
        file.
      </WarningLabel>
    </StyledNuvoBox>
  );
};

export default React.memo(MapMandatoryFieldsStep);
